import { m } from "@nerves/i18n/messages";
import type { CatalogScope, DiscordChannelView, RouteView } from "@nerves/shared";
import { ArrowsClockwise, ChatsCircle, Folders, Hash } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Page, Panel } from "@/components/shell/Page";
import {
  Button,
  buttonStyles,
  Callout,
  ConfirmDialog,
  InlineError,
  RelativeTime,
  RowSkeleton,
  Select,
} from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { cx } from "@/lib/cx";
import { keys, loadMe, routingQuery } from "@/lib/queries";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_app/routing")({
  beforeLoad: async ({ context }) => {
    if (!(await loadMe(context.queryClient))?.isAdmin) throw redirect({ to: "/" });
  },
  component: RoutingPage,
});

type Scope = RouteView["scope"];
interface Pending {
  scope: Scope;
  scopeKey: string;
  channel: DiscordChannelView;
}

function channelLabel(c: DiscordChannelView) {
  return `${c.guildName} / #${c.name}`;
}

/** One row of the tree: the scope, what it uses today, a channel picker. */
function RouteRow({
  scope,
  scopeKey,
  name,
  description,
  route,
  inherited,
  channels,
  level,
  onPick,
  onClear,
}: {
  scope: Scope;
  scopeKey: string;
  name: string;
  description?: string;
  route: RouteView | undefined;
  inherited: string;
  channels: DiscordChannelView[];
  level: 0 | 1;
  onPick: (p: Pending) => void;
  onClear: (scope: Scope, key: string) => void;
}) {
  const usable = channels.filter((c) => c.missing.length === 0);
  const blocked = channels.filter((c) => c.missing.length > 0);
  const current = route ? channels.find((c) => c.channelId === route.channelId) : undefined;
  const icon =
    scope === "product" ? (
      <Hash size={16} aria-hidden="true" />
    ) : scope === "space" ? (
      <Folders size={16} aria-hidden="true" />
    ) : (
      <ChatsCircle size={16} aria-hidden="true" />
    );
  return (
    <li
      className={cx(
        "flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-b-0",
        level === 1 && "pl-10",
      )}
    >
      <span className="text-fg-muted">{icon}</span>
      <div className="flex min-w-48 flex-1 flex-col">
        <span className="text-sm font-medium">
          {name} {scope !== "default" ? <span className="font-mono text-xs text-fg-subtle">{scopeKey}</span> : null}
        </span>
        <span className="text-xs text-fg-muted">
          {description ??
            (route
              ? `${current ? channelLabel(current) : `#${route.channelName ?? route.channelId}`} · ${route.channelKind === "forum" ? m.routing_forum() : m.routing_text()}`
              : m.routing_inherits({ target: inherited }))}
        </span>
      </div>
      <Select
        label={m.routing_channel({ name })}
        labelHidden
        className="w-64 max-w-full"
        placeholder={m.routing_choose()}
        items={usable.map((c) => ({
          id: c.channelId,
          label: `${channelLabel(c)} (${c.kind === "forum" ? m.routing_forum() : m.routing_text()})`,
        }))}
        selectedKey={route?.channelId ?? null}
        onSelectionChange={(id) => {
          const channel = usable.find((c) => c.channelId === id);
          if (channel && channel.channelId !== route?.channelId) onPick({ scope, scopeKey, channel });
        }}
      />
      {route ? (
        <Button size="sm" variant="ghost" onPress={() => onClear(scope, scopeKey)}>
          {m.routing_clear()}
        </Button>
      ) : null}
      {blocked.length > 0 && level === 0 && scope === "default" ? (
        <ul className="w-full text-xs text-warning-text">
          {blocked.map((c) => (
            <li key={c.channelId}>
              {channelLabel(c)}: {m.routing_missing({ perms: c.missing.join(", ") })}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function RoutingPage() {
  const qc = useQueryClient();
  const query = useQuery(routingQuery);
  const [pending, setPending] = useState<Pending | null>(null);

  const save = async (p: Pending, moveOpen: boolean) => {
    await api.put("/api/v1/routing/routes", {
      scope: p.scope,
      scopeKey: p.scopeKey,
      channelId: p.channel.channelId,
      moveOpen,
    });
    toast.show({ title: m.routing_saved(), tone: "success" });
    await qc.invalidateQueries({ queryKey: keys.routing });
  };
  const clear = useMutation({
    mutationFn: ({ scope, key }: { scope: Scope; key: string }) =>
      api.delete(`/api/v1/routing/routes/${scope}/${encodeURIComponent(key)}`),
    onSuccess: () => {
      toast.show({ title: m.routing_removed(), tone: "success" });
      void qc.invalidateQueries({ queryKey: keys.routing });
    },
  });
  const sync = useMutation({
    mutationFn: () => api.post("/api/v1/routing/sync"),
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.routing }),
  });

  const data = query.data;
  const tree = useMemo(() => {
    const spaces = (data?.catalog ?? []).filter((s) => s.kind === "space");
    return spaces.map((s) => ({
      space: s,
      products: (data?.catalog ?? []).filter((p) => p.kind === "product" && p.spaceKey === s.key),
    }));
  }, [data]);
  const routeOf = (scope: Scope, key: string) => data?.routes.find((r) => r.scope === scope && r.scopeKey === key);

  const actions = (
    <Button size="sm" onPress={() => sync.mutate()} isPending={sync.isPending}>
      <ArrowsClockwise size={14} aria-hidden="true" />
      {m.routing_sync()}
    </Button>
  );

  return (
    <Page title={m.routing_title()} intro={m.routing_intro()} actions={actions}>
      {query.isPending ? (
        <RowSkeleton rows={5} />
      ) : query.isError ? (
        <InlineError message={m.error_generic()} onRetry={() => void query.refetch()} />
      ) : (
        <>
          {!data!.discord.connected ? (
            <Callout
              tone="warning"
              title={m.routing_bot_offline_title()}
              actions={
                data!.discord.inviteUrl ? (
                  <a
                    href={data!.discord.inviteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={buttonStyles({ size: "sm" })}
                  >
                    {m.routing_invite()}
                  </a>
                ) : null
              }
            >
              {m.routing_bot_offline_body()}
            </Callout>
          ) : data!.discord.inviteUrl ? (
            <p className="text-xs text-fg-muted">
              <a
                href={data!.discord.inviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                {m.routing_invite()}
              </a>
            </p>
          ) : null}
          <p className="text-xs text-fg-muted" role="status">
            {sync.isError ? (
              m.routing_sync_error({ error: sync.error instanceof ApiError ? sync.error.message : "?" })
            ) : data!.catalogError ? (
              m.routing_sync_error({ error: data!.catalogError })
            ) : data!.catalogSyncedAt ? (
              <>
                {m.routing_synced({ time: "" }).trim()} <RelativeTime value={data!.catalogSyncedAt} />
              </>
            ) : (
              m.routing_never_synced()
            )}
          </p>
          <Panel>
            <ul aria-label={m.routing_title()}>
              <RouteRow
                scope="default"
                scopeKey="*"
                name={m.routing_default()}
                description={routeOf("default", "*") ? undefined : m.routing_default_desc()}
                route={routeOf("default", "*")}
                inherited={m.routing_none()}
                channels={data!.channels}
                level={0}
                onPick={setPending}
                onClear={(scope, key) => clear.mutate({ scope, key })}
              />
              {tree.map(({ space, products }: { space: CatalogScope; products: CatalogScope[] }) => {
                const spaceRoute = routeOf("space", space.key);
                const fallback = routeOf("default", "*") ? m.routing_inherits_default() : m.routing_none();
                return [
                  <RouteRow
                    key={space.key}
                    scope="space"
                    scopeKey={space.key}
                    name={space.name}
                    route={spaceRoute}
                    inherited={fallback}
                    channels={data!.channels}
                    level={0}
                    onPick={setPending}
                    onClear={(scope, key) => clear.mutate({ scope, key })}
                  />,
                  ...products.map((p) => (
                    <RouteRow
                      key={p.key}
                      scope="product"
                      scopeKey={p.key}
                      name={p.name}
                      route={routeOf("product", p.key)}
                      inherited={spaceRoute ? m.routing_inherits_space() : fallback}
                      channels={data!.channels}
                      level={1}
                      onPick={setPending}
                      onClear={(scope, key) => clear.mutate({ scope, key })}
                    />
                  )),
                ];
              })}
            </ul>
          </Panel>
          {tree.length === 0 ? <p className="text-sm text-fg-muted">{m.routing_no_catalog()}</p> : null}
        </>
      )}

      {/* A choice, not an "are you sure": both answers are fine, the default keeps open threads where they are. */}
      <ConfirmDialog
        isOpen={Boolean(pending)}
        onOpenChange={(open) => !open && setPending(null)}
        title={m.routing_move_title()}
        description={m.routing_move_body({ channel: pending?.channel.name ?? "" })}
        confirmLabel={m.routing_move_yes()}
        tone="primary"
        onConfirm={() => save(pending!, true)}
      >
        <Button
          variant="secondary"
          onPress={async () => {
            if (!pending) return;
            await save(pending, false);
            setPending(null);
          }}
        >
          {m.routing_move_no()}
        </Button>
      </ConfirmDialog>
    </Page>
  );
}
